import useStore from '@/store/useStore';
import colors from '@/styles/colors';
import beTwMerge from '@/utils/beTwMerge';
import { useEffect, useRef } from 'react';

export type PromoTileProps = {
  ctaLabel?: string;
  isFullBleed?: boolean;
  isSetMaxWidth?: boolean;
};

export default function PromoTile({ ctaLabel, isFullBleed = false, isSetMaxWidth = true }: PromoTileProps) {
  const mobileImageRef = useRef<any>(null);
  const promo = useStore(state => state.builderData?.promo);
  const gateway = promo?.gateway;
  const setIsPromoModalVisible = useStore(state => state.setIsPromoModalVisible);

  useEffect(() => {
    if (!mobileImageRef?.current) return;
    const imageHeight = mobileImageRef.current.offsetHeight;
    mobileImageRef.current.style.width = `${imageHeight * 0.8}px`;
  }, [mobileImageRef.current]);

  if (!gateway) return null;

  const isShowDesktopBackgroundImage = gateway.desktop?.background_image.url;
  const isShowMobileBackgroundImage = gateway.mobile?.background_image.url;

  return (
    <div
      // eslint-disable-next-line react/no-unknown-property
      gem-price-ignore=""
      className={beTwMerge(
        'min-[1504px]:tw-w-full tw-min-h-[135px]',
        !isFullBleed && 'tw-mx-4 lg:tw-mx-8 min-[1504px]:tw-w-[calc(100%-64px)]',
        isSetMaxWidth && 'tw-max-w-[1440px] min-[1504px]:tw-mx-auto',
        isShowDesktopBackgroundImage && 'tw-min-h-[70px]'
      )}
      data-ge-hide=""
    >
      {/* Tablet & Desktop Content */}
      <div
        style={{
          backgroundColor: gateway.desktop?.background_hex_color || colors['dark-green'].raw,
        }}
        className={`tw-hidden md:tw-flex tw-relative tw-text-color-white lg:tw-max-h-[135px] ${isShowDesktopBackgroundImage && 'tw-aspect-[280/27] tw-max-h-none lg:tw-max-h-none'}`}
      >
        {isShowDesktopBackgroundImage && (
          <img
            src={gateway.desktop?.background_image.url}
            srcSet={`${gateway.desktop?.background_image.url_2x} 2x`}
            alt={gateway.desktop?.background_image.alt || ''}
            loading="lazy"
            className="tw-w-full tw-h-full tw-self-stretch tw-absolute tw-top-0 tw-left-0 tw-object-cover"
          />
        )}
        <a
          href={gateway.desktop?.tile_link}
          className={`tw-flex tw-w-full tw-flex-grow tw-items-stretch tw-ml-0 md:tw-mx-auto lg:tw-ml-[22%] tw-cursor-pointer hover:tw-no-underline md:tw-items-center ${isShowDesktopBackgroundImage && 'md:tw-ml-[22%]'}`}
        >
          {!isShowDesktopBackgroundImage && (
            <img
              src={gateway.desktop?.image.url}
              srcSet={`${gateway.desktop?.image.url_2x} 2x`}
              alt={gateway.desktop?.image.alt || ''}
              loading="lazy"
              className="tw-z-[1] tw-h-auto tw-max-h-[135px] tw-self-stretch tw-my-4 md:tw-my-0 tw-object-cover"
              style={{
                maxHeight: isShowDesktopBackgroundImage ? 'none' : undefined,
                aspectRatio: isShowDesktopBackgroundImage && !gateway.desktop?.image.url ? 44 / 27 : undefined,
              }}
            />
          )}
          <div
            className={`tw-p-4 tw-flex-grow tw-relative ${isShowDesktopBackgroundImage ? 'tw-h-full' : 'tw-h-[146.2px]'}`}
          >
            <div
              // eslint-disable-next-line react/no-unknown-property
              gem-price-ignore=""
              className="h4 tw-my-3 tw-font-avenirmedium"
              style={{
                color: gateway.desktop?.header?.color || 'white',
                fontSize: gateway.desktop?.header?.['font-size'],
                fontFamily: gateway.desktop?.header?.['font-family'],
              }}
              dangerouslySetInnerHTML={{ __html: gateway.desktop?.header.text! }}
            />
            <div
              // eslint-disable-next-line react/no-unknown-property
              gem-price-ignore=""
              className="home-banner-promo__subheader tw-leading-normal tw-text-sm tw-mb-4 tw-font-book"
              style={{
                color: gateway.desktop?.subheader?.color || 'white',
                fontSize: gateway.desktop?.subheader?.['font-size'],
                fontFamily: gateway.desktop?.subheader?.['font-family'],
              }}
              dangerouslySetInnerHTML={{ __html: gateway.desktop?.subheader.text! }}
            />
          </div>
        </a>

        {ctaLabel && (
          <div
            className={`tw-text-xs tw-absolute tw-bottom-[8px] tw-cursor-pointer ${isFullBleed ? 'tw-right-[21px] large:tw-right-[47px]' : 'tw-right-[8px]'}`}
          >
            <a
              data-toggle="modal"
              data-target="#popup-gift-promo"
              className="see-details-link tw-text-color-white tw-cursor-pointer tw-underline focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color] tw-font-book"
              id="hp-terms-link"
              onClick={() => setIsPromoModalVisible(true)}
              style={{
                color: gateway.desktop?.t_c?.color || 'white',
                fontSize: gateway.desktop?.t_c?.['font-size'],
                fontFamily: gateway.desktop?.t_c?.['font-family'],
              }}
              dangerouslySetInnerHTML={{ __html: gateway.desktop?.t_c.text! }}
              role="button"
              tabIndex={0}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  setIsPromoModalVisible(true);
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Mobile Content */}
      <div
        style={{ backgroundColor: gateway.mobile?.background_hex_color || colors['dark-green'].raw }}
        className={`tw-text-color-white tw-bg-color-dark-green tw-relative tw-flex md:tw-hidden ${isShowMobileBackgroundImage ? 'tw-aspect-[95/46]' : ''}`}
      >
        {isShowMobileBackgroundImage && (
          <img
            src={gateway.mobile?.background_image.url}
            srcSet={`${gateway.mobile?.background_image.url_2x} 2x`}
            alt={gateway.mobile?.background_image.alt || ''}
            loading="lazy"
            className="tw-w-full tw-h-full tw-self-stretch tw-absolute tw-top-0 tw-left-0 tw-object-cover"
          />
        )}
        <a
          href={gateway.mobile?.tile_link}
          className="tw-flex tw-w-full tw-flex-grow tw-ml-0 md:tw-ml-[12%] lg:tw-ml-[22%] tw-cursor-pointer hover:tw-no-underline"
        >
          {!isShowMobileBackgroundImage && (
            <img
              ref={mobileImageRef}
              src={gateway.mobile?.image.url}
              srcSet={`${gateway.mobile?.image.url_2x} 2x`}
              alt={gateway.mobile?.image.alt || ''}
              loading="lazy"
              className="tw-z-[1] tw-max-w-[135px] tw-max-h-[168px] tw-w-full tw-object-cover"
              style={{
                height: isShowMobileBackgroundImage ? '100%' : undefined,
                maxHeight: isShowMobileBackgroundImage ? 'none' : undefined,
                maxWidth: isShowMobileBackgroundImage ? 'none' : undefined,
                opacity: !gateway.mobile?.image.url ? 0 : undefined,
              }}
            />
          )}
          <div
            className={`tw-py-[13.5px] tw-px-[16px] tw-flex-grow tw-relative tw-flex tw-flex-col tw-justify-center ${isShowMobileBackgroundImage ? 'tw-h-full' : 'tw-h-[168px]'}`}
          >
            <div
              className="h4 tw-my-2 tw-self-start tw-font-avenirmedium"
              style={{
                color: gateway.mobile?.header?.color || 'white',
                fontSize: gateway.mobile?.header?.['font-size'],
                fontFamily: gateway.mobile?.header?.['font-family'],
              }}
              dangerouslySetInnerHTML={{ __html: gateway.mobile?.header.text! }}
            />
            <div
              className="home-banner-promo__subheader tw-text-sm tw-leading-normal tw-mb-2 tw-font-book"
              style={{
                color: gateway.mobile?.subheader?.color || 'white',
                fontSize: gateway.mobile?.subheader?.['font-size'],
                fontFamily: gateway.mobile?.subheader?.['font-family'],
              }}
              dangerouslySetInnerHTML={{ __html: gateway.mobile?.subheader.text! }}
            />
          </div>
        </a>

        {ctaLabel && (
          <div
            className={`tw-text-xs tw-absolute tw-bottom-[8px] tw-cursor-pointer ${isFullBleed ? 'tw-right-[21px]' : 'tw-right-[8px]'}`}
          >
            <a
              data-toggle="modal"
              data-target="#popup-gift-promo"
              className="see-details-link tw-text-color-white tw-cursor-pointer tw-underline focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color] tw-font-book"
              id="hp-terms-link"
              onClick={() => setIsPromoModalVisible(true)}
              style={{
                color: gateway.mobile?.t_c?.color || 'white',
                fontSize: gateway.mobile?.t_c?.['font-size'],
                fontFamily: gateway.mobile?.t_c?.['font-family'],
              }}
              dangerouslySetInnerHTML={{ __html: gateway.mobile?.t_c.text! }}
              role="button"
              tabIndex={0}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  setIsPromoModalVisible(true);
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
